/* eslint-disable max-len */
import React, { useMemo, useEffect } from 'react';
import {
  ButtonPrimary,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  dlsBrightBlue,
  Anchor,
  IconDocument,
  IconLock,
  IconTelephone,
} from '@americanexpress/dls-react';
import { useReactlytics } from '@americanexpress/reactlytics';
import PropTypes from 'prop-types';
import {
  FormattedMessage, createIntlCache, createIntl, RawIntlProvider, useIntl,
} from 'react-intl';
// eslint-disable-next-line import/no-extraneous-dependencies
import { useProductContext } from '../Product';
import bybCopy from '../../../locale/de-DE/bybCopy.json';
import { instructionsLeft, instructionsRight } from '../FieldOptions/BYBInstructions';
import createAnalyticsData from '../../utils/createAnalyticsData';

const cache = createIntlCache();

const bybIntl = createIntl({
  locale: 'de-DE',
  messages: bybCopy,
}, cache);

const BYBModal = ({ toggleBYBModal }) => {
  const { product, product: { basicProductName, telephoneNumber } } = useProductContext();
  const {
    messages: {
      links,
      links: {
        bybLeftInstructionSecondLink,
      },
    },
  } = useIntl();

  const leftInstructionsList = useMemo(
    () => instructionsLeft.map((obj) => {
      if (obj.id === 2) {
        return (
          <li className="font-weight-bold" key={obj.id}>
            <span className="pad-1-b pad-1-t">
              <FormattedMessage id="byb.leftInstruction.second.subText1" />
            </span>
            <span>
              <a href={bybLeftInstructionSecondLink} target="_blank" className="text-underline" aria-label="papericonlink" rel="noopener noreferrer">
                <FormattedMessage id="byb.leftInstruction.second.link" />
              </a>
            </span>
            <span>
              <FormattedMessage id="byb.leftInstruction.second.subText2" />
            </span>
          </li>
        );
      }
      return <li key={obj.id}><FormattedMessage id={obj.value} /></li>;
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }), []);

  const rightInstructionsList = useMemo(
    () => instructionsRight.map((obj) => (
      <li key={obj.id}><FormattedMessage id={obj.value} /></li>
    )), []);

  const { track } = useReactlytics();
  useEffect(() => {
    track(createAnalyticsData({
      element: 'BYB',
      detail: ' ',
      product,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <RawIntlProvider value={bybIntl}>
      <Modal
        onClose={toggleBYBModal}
        theme={{
          // allows the modal to be scrollable but scrollbar not to be shown
          '& > div': {
            maxWidth: '59rem',
            overflowY: 'auto',
            msOverflowStyle: 'none',
            scrollbarWidth: 'none',
            '::--webkit-scrollbar': { display: 'none' },
          },
        }}
      >
        <ModalHeader
          closeButtonLabel="Close"
          dismissible={true}
          type="Close"
          closeButtonProps={{ id: 'closeButton', type: 'Close' }}
        >
          <h2 className="fluid heading-3">
            <FormattedMessage id="header.title" />
          </h2>
        </ModalHeader>
        <ModalBody>
          <div className="pad-3-lr pad-3-tb">
            <h2 className="fluid heading-5">
              <FormattedMessage id="body.title" />
            </h2>
            <div className="row">
              <div className="col-md-7 col-sm-12 margin-3-tb">
                <p className="body-1">
                  <FormattedMessage id="body.content" />
                </p>
                <br />
                <ul>
                  <li className="font-weight-bold" id={0}><FormattedMessage id={`productSpecific.${basicProductName}`} /></li>
                  {leftInstructionsList}
                </ul>
              </div>
              <div className="col-md-5 col-sm-12">
                <div className="margin-3-t pad-3-l-md-up">
                  <ul>
                    {rightInstructionsList}
                  </ul>
                  <h2 className="heading-3 margin-2-t">
                    <FormattedMessage id="impInfo.header" />
                  </h2>
                  <div className="margin-2-t flex flex-column flex-justify-center">
                    <ul style={{ listStyle: 'none', padding: '0' }}>
                      <li className="flex flex-row flex-align-items-center">
                        <IconDocument
                          className="pad-1-r"
                          theme={dlsBrightBlue}
                          title="IconDocument icon"
                        />
                        <Anchor
                          href={links[basicProductName].paperIconLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          reactlytics={{ element: 'MembershipConditions', detail: 'BYB' }}
                        >
                          <FormattedMessage id="MembershipConditions" />
                        </Anchor>
                      </li>
                      <li className="flex flex-row flex-align-items-center">
                        <IconLock
                          className="pad-1-r"
                          theme={dlsBrightBlue}
                          title="IconLock icon"
                        />
                        <Anchor
                          href={links.lockIconLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          reactlytics={{ element: 'DataProtectionDeclaration', detail: 'BYB' }}
                        >
                          <FormattedMessage id="DataProtectionDeclaration" />
                        </Anchor>
                      </li>
                      <li className="flex flex-row flex-align-items-center">
                        <IconTelephone
                          className="pad-1-r"
                          theme={dlsBrightBlue}
                          title="IconTelephone icon"
                        />
                        <Anchor href={`tel:${telephoneNumber}`} rel="noreferrer noopener" target="_blank" reactlytics={{ element: 'CallNumber', detail: 'BYB' }}>
                          <FormattedMessage
                            id="contactNumber.text"
                            values={{ telephoneNumber }}
                          />
                        </Anchor>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </ModalBody>
        <ModalFooter>
          <ButtonPrimary onClick={toggleBYBModal} reactlytics={{ element: 'Close', detail: 'BYB' }}>
            <FormattedMessage id="byb.continue" />
          </ButtonPrimary>
        </ModalFooter>
      </Modal>
    </RawIntlProvider>
  );
};

export default BYBModal;

BYBModal.propTypes = {
  toggleBYBModal: PropTypes.func.isRequired,
};
